import { deriveSubkeys, decryptContent, aesGcmOpen, unwrap, passwordAccessToken, linkAccessToken, fromBase64Url, hkdf } from '../crypto/activityCrypto.js';

/**
 * Alerte lancée pendant un rendez-vous : son lien est dérivé du lien de secours de
 * la personne qui alerte (même calcul que RendezVousAlertLinker côté téléphone).
 */
export async function rdvAlertLinkSecret(backupSecret) {
    return hkdf(backupSecret, 'rdv-alert-link', 32);
}

const safeJson = text => { try { return JSON.parse(text); } catch (_) { return null; } };

/**
 * Rendez-vous v2 : le serveur ne remet que des chiffrés. Avec la clé du RDV
 * (ouverte par le lien de secours), on reconstitue le détail au format attendu
 * par rendezVousAdminToBundle.
 */
export async function openRendezVousV2(detail, activityKey) {
    const keys = await deriveSubkeys(activityKey);
    const attempt = async (fn) => { try { return await fn(); } catch (_) { return null; } };
    const meeting = detail.encryptedMeeting
        ? safeJson(await attempt(() => decryptContent(keys.events, detail.encryptedMeeting))) || {}
        : {};
    const meta = detail.meetingMeta || {};

    const messages = [];
    for (const message of detail.messages || []) {
        const text = message.ciphertext
            ? (await attempt(() => decryptContent(keys.messages, message.ciphertext))) ?? '[message illisible]'
            : message.message;
        messages.push({ ...message, message: text, senderName: message.senderName || (message.senderUserId === detail.creator?.id ? meta.creatorDisplayName : meta.participantDisplayName) });
    }

    const locations = [];
    for (const location of detail.locations || []) {
        const point = safeJson(await attempt(() => decryptContent(keys.events, location.encryptedPoint)));
        if (point && Number.isFinite(point.latitude) && Number.isFinite(point.longitude)) {
            locations.push({ userId: location.userId, latitude: point.latitude, longitude: point.longitude,
                accuracy: point.accuracy ?? null, inZone: location.inZone, recordedAt: location.recordedAt });
        }
    }

    return {
        // Clé du RDV jointe pour la clé de lecture du dossier de preuve (expert).
        keys: { ...keys, activityKey: activityKey instanceof Uint8Array ? activityKey : null },
        detail: {
            ...detail,
            address: meeting.address || null,
            latitude: meeting.latitude ?? null,
            longitude: meeting.longitude ?? null,
            zonePolygon: meeting.zonePolygon || null,
            messages,
            locations,
        },
    };
}

/** Photo v2 : « GCM1 » ‖ nonce ‖ chiffré ‖ tag (binaire), sous-clé des événements. */
export async function decryptPhoto(bytes, eventsKey) {
    const data = new Uint8Array(bytes);
    const magic = new TextDecoder().decode(data.slice(0, 4));
    if (magic !== 'GCM1') return data;
    return aesGcmOpen(eventsKey, data.slice(4));
}

/** Lien police d'un rendez-vous : /relecture-rdv/<id>#s=<sel>&i=<itérations> ; le mot de passe est transmis à part. */
export function parseRendezVousReplayLink(link) {
    let url;
    try { url = new URL(String(link || '').trim()); } catch (_) { return null; }
    const match = /\/relecture-rdv\/([^/]+)/.exec(url.pathname);
    const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
    const iterations = Number(fragment.get('i'));
    let salt;
    try { salt = fromBase64Url(fragment.get('s') || ''); } catch (_) { return null; }
    if (!match || salt.length !== 16 || !Number.isInteger(iterations) || iterations < 100_000) return null;
    return { rdvId: decodeURIComponent(match[1]), salt, iterations };
}

/**
 * Ouverture par lien police : le jeton dérivé du mot de passe donne accès au
 * contenu chiffré et à l'enveloppe, que le mot de passe ouvre ici. Le serveur
 * ne voit ni le mot de passe ni la clé.
 */
export async function openRendezVousForPolice({ apiUrl, rdvId, password, salt, iterations }) {
    const headers = { 'X-Activity-Access': await passwordAccessToken(password, salt, iterations) };
    const response = await fetch(`${apiUrl.replace(/\/+$/, '')}/api/rendez-vous/${encodeURIComponent(rdvId)}/enquete`, { headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(response.status === 403 ? 'Accès refusé' : (data.message || `Erreur ${response.status}`));
    const activityKey = await unwrap(data.envelope, rdvId, { password });
    const opened = await openRendezVousV2(data.rendezVous, activityKey);
    return { headers, detail: opened.detail, keys: opened.keys, label: data.label || null, expiresAt: data.expiresAt || null };
}

/**
 * Lien complet d'un rendez-vous : secours (/rdv-secours/<identifiant>#k=…) ou
 * invitation (/rdv/<jeton>#k=…). Le secret reste dans ce navigateur.
 */
export function parseRendezVousLink(link) {
    let url;
    try { url = new URL(String(link || '').trim()); } catch (_) { return null; }
    const match = /\/(rdv-secours|rdv)\/([^/#?]+)/.exec(url.pathname);
    const secretText = new URLSearchParams(url.hash.replace(/^#/, '')).get('k');
    if (!match || !secretText) return null;
    let linkSecret;
    try { linkSecret = fromBase64Url(secretText); } catch (_) { return null; }
    if (linkSecret.length < 16) return null;
    return { ref: decodeURIComponent(match[2]), kind: match[1] === 'rdv-secours' ? 'secours' : 'invitation', linkSecret };
}

/** Dashboard : ouvre un rendez-vous v2 avec son lien complet ; le serveur ne voit que le jeton dérivé. */
export async function openRendezVousWithLink({ apiUrl, ref, linkSecret, headers = {} }) {
    const access = { ...headers, 'X-Activity-Access': await linkAccessToken(linkSecret) };
    const response = await fetch(`${apiUrl.replace(/\/+$/, '')}/admin/rendez-vous/par-lien/${encodeURIComponent(ref)}`, { headers: access });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || `Erreur ${response.status}`);
    const activityKey = await unwrap(data.envelope, data.rdvId, { linkSecret });
    const opened = await openRendezVousV2(data.rendezVous, activityKey);
    return { rdvId: data.rdvId, raw: data.rendezVous, detail: opened.detail, keys: opened.keys };
}
